import { call, put, select, takeLatest } from 'redux-saga/effects'
import i18n from '../../../../i18n'
import { AppRouterConstants } from '../../../../components/appRouter/AppRouter.constants'
import { showToast } from '../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../components/toast/store/toast.constants'
import { getAllPCRDocs } from '../../../home/store/home.actions'
import { getAuthenticationJwtSelector } from '../../../login/selectors/getAuthenticationJwt.selector'
import { getRouteOrigin, navigateReplace } from '../../../../utilities/navigation.util'
import { QaAddendumService } from '../services/qaAddendum.service'
import { ZollAPIService } from '../../../../services/zollApi.service'
import { getQaAddendumSelector } from '../selectors'
import { IQaAddendumResponseResult } from '../store/qaAddendum.types'
import { QaAddendumConstants } from '../store/qaAddendum.constants'
import { setQaAddendumSubmitting, setSelectedFlagId } from '../store/qaAddendum.actions'
import { removeNotification } from '../../../notificationCenter/store/notificationCenter.actions'
import { resetLoader, showLoader } from '../../../../components/loader/store/loader.actions'
import {
  handleApiError,
  setErrorToastDuration,
} from '../../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import Logger from '../../../../services/logger.service'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'

function* saveQaAddendum() {
  const { selectedFlagId, addendumText } = yield select(getQaAddendumSelector)

  if (!selectedFlagId || !addendumText.trim()) {
    yield put(setQaAddendumSubmitting(false))
    return
  }

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const qaAddendumService = new QaAddendumService(authenticationJwt)

  yield put(showLoader({ isOpen: true, message: i18n.t('qaFlags.qaAddendum.flagResolution.submitting') }))
  const origin = getRouteOrigin()
  const wasInsidePcr = !origin || origin.startsWith(AppRouterConstants.TABS_ROUTE)
  const navigationTarget = wasInsidePcr ? AppRouterConstants.HOME_ROUTE : (origin as string)
  try {
    const response: IQaAddendumResponseResult = yield call(
      [qaAddendumService, qaAddendumService.submitQaFlagAddendum],
      selectedFlagId,
      addendumText
    )

    if (response.status === 200) {
      yield put(removeNotification(Number(selectedFlagId)))
      yield put(
        showToast({
          message: i18n.t(
            wasInsidePcr
              ? 'qaFlags.qaAddendum.flagResolution.submitSuccessSelectAnother'
              : 'qaFlags.qaAddendum.flagResolution.submitSuccess',
            { flagId: selectedFlagId }
          ),
          duration: 2500,
          position: 'top',
          color: ToastColor.Success,
        })
      )
      yield put(setSelectedFlagId(null))
      yield put(getAllPCRDocs())
      navigateReplace(navigationTarget)
      Logger.info(`QaAddendum: saveQaAddendum success, flagId: ${selectedFlagId}`)
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage = i18n.t('qaFlags.qaAddendum.flagResolution.submitError')
      }
      yield put(handleApiError(response))
      navigateReplace(navigationTarget)
    }
  } catch (e: unknown) {
    const cause = e instanceof Error ? e.message : 'unknown'
    yield put(setErrorToastDuration(2000))
    yield put(
      handleApiError(
        ZollAPIService.createToastLogErrorResponse(i18n.t('qaFlags.qaAddendum.flagResolution.submitFailed', { cause }))
      )
    )
    navigateReplace(navigationTarget)
  } finally {
    yield put(resetLoader())
    yield put(setQaAddendumSubmitting(false))
  }
}

export function* saveQaAddendumSaga() {
  yield takeLatest([QaAddendumConstants.SUBMIT_QA_FLAG_ADDENDUM], performWithMutexHOG, saveQaAddendum)
}
